"use client";

import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { LayoutGridIcon, ListIcon, SearchIcon } from "lucide-react";

import { cx } from "@tripcart/traveler-ui/cx";
import { Input } from "@tripcart/traveler-ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tripcart/traveler-ui/components/select";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@tripcart/traveler-ui/components/toggle-group";

import { useTranslator } from "@/lib/translation-context";
import { SORT_OPTIONS, parseState } from "./_filters-shared";
import styles from "./_results-controls.client.module.css";

// Built once: Select re-walks its children for labels whenever their
// identity changes, and this list never does.
const SORT_ITEMS = SORT_OPTIONS.map((o) => (
  <SelectItem key={o.value} value={o.value}>
    {o.label}
  </SelectItem>
));

export function ResultsControls(): React.ReactElement {
  const t = useTranslator();
  const router = useRouter();
  const sp = useSearchParams();
  const state = parseState((k) => sp.get(k) ?? undefined);
  const [isPending, startTransition] = useTransition();

  // View is a presentation-only choice — it never changes the API query — so
  // toggling it must not trigger a server round-trip. Local state drives
  // instant button feedback; `handleViewChange` mirrors it into the URL (via
  // history.replaceState, no refetch) so the choice is shareable, survives
  // back/forward, and rides along on every sort/search/filter navigation.
  const [view, setView] = useState<"grid" | "list">(state.view);

  // Prefetch the INACTIVE view so the first toggle paints from the router
  // cache instead of a fresh round-trip (staleTimes then keeps it warm for
  // subsequent switches). View is presentation-only — it never changes the
  // API query — so this warms the layout swap with no extra data fetch. Runs
  // on mount and whenever the target URL changes (filters/sort/view).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (view === "grid") p.delete("view");
    else p.set("view", "grid");
    const qs = p.toString();
    router.prefetch(
      qs ? `${window.location.pathname}?${qs}` : window.location.pathname,
    );
  }, [router, view, sp]);

  function setParam(key: string, value: string | null, resetPage = true): void {
    const p = new URLSearchParams(sp.toString());
    if (!value) p.delete(key);
    else p.set(key, value);
    if (resetPage) p.delete("page");
    // Preserve the active layout across server-driven param changes, so the
    // re-rendered page keeps the chosen view instead of snapping to the list
    // default. List carries no param.
    if (view === "grid") p.set("view", "grid");
    else p.delete("view");
    // Transition keeps the current results visible (no skeleton flash) while
    // the server returns the new page; `isPending` drives the dim below.
    startTransition(() => {
      router.replace(`?${p.toString()}`, { scroll: false });
    });
  }

  // Debounced search → ?q
  const [query, setQuery] = useState(state.q);
  const firstRender = useRef(true);
  useEffect(() => {
    setQuery(state.q);
  }, [state.q]);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    const t = setTimeout(() => {
      if (query.trim() !== state.q) setParam("q", query.trim() || null);
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const handleViewChange = useCallback(
    (next: "grid" | "list") => {
      // Only the active view is server-rendered now, so switch via a router
      // transition. `setView` gives the toggle button instant active-state
      // feedback while the RSC resolves; staleTimes caches the other view's
      // payload so every switch after the first is instant. The choice rides
      // in `?view=`, so it stays shareable and survives back/forward.
      setView(next);
      const params = new URLSearchParams(window.location.search);
      if (next === "grid") params.set("view", "grid");
      else params.delete("view");
      const qs = params.toString();
      startTransition(() => {
        router.replace(qs ? `?${qs}` : window.location.pathname, {
          scroll: false,
        });
      });
    },
    [router],
  );

  return (
    <div
      aria-busy={isPending}
      // Mobile: search full-width on row 1, then a second full-width row where
      // the sort select grows and the view toggle pins right — so both rows
      // share the same left/right edges. Collapses to one row on sm+.
      className={cx(styles.root, isPending && styles.pending)}
    >
      <div className={styles.search}>
        <SearchIcon className={styles.searchIcon} strokeWidth={2} aria-hidden />
        <Input
          type="search"
          size="lg"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("Search tours, destinations…")}
          aria-label={t("Search Tours")}
          className={styles.searchInput}
        />
      </div>

      <div className={styles.tools}>
        <div className={styles.sort}>
          <Select value={state.sort} onValueChange={(v) => setParam("sort", v)}>
            <SelectTrigger
              size="lg"
              aria-label={t("Sort Tours")}
              className={styles.select}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">{SORT_ITEMS}</SelectContent>
          </Select>
        </div>

        <ToggleGroup value={view} onValueChange={handleViewChange}>
          <ToggleGroupItem value="list" aria-label={t("List View")}>
            <ListIcon strokeWidth={1.8} aria-hidden />
          </ToggleGroupItem>
          <ToggleGroupItem value="grid" aria-label={t("Grid View")}>
            <LayoutGridIcon strokeWidth={1.8} aria-hidden />
          </ToggleGroupItem>
        </ToggleGroup>
      </div>
    </div>
  );
}
